import type { Locale } from "@tutor/protocol";
import { useCallback } from "react";
import { useLocale } from "../i18n";

// Strings for parent mode, in the same `{name}` placeholder style as `../i18n.ts`.
// Parents read these, so the copy can be a little longer than the child's.

const en = {
  "parent.open": "Parent",
  "parent.openLabel": "Open parent mode",
  "parent.brand": "Tutor by Probability Lab",
  "parent.title": "Parent mode",
  "parent.leave": "Back to the lesson",
  "parent.loading": "Loading…",
  "parent.retry": "Try again",
  "parent.offline": "You're offline. Check the connection and try again.",
  "parent.rateLimited": "Too many tries in a row. Please wait a few minutes.",
  "parent.error": "Something went wrong. Please try again.",

  "signin.title": "Parent sign-in",
  "signin.body": "Parent mode needs a code sent to your email address.",
  "signin.expired": "For safety, please sign in again with a new code.",
  "signin.emailLabel": "Your email address",
  "signin.invalidEmail": "Please enter a valid email address.",
  "signin.sendCode": "Send me a code",
  "signin.codeBody": "Type the 6-digit code we sent to {email}.",
  "signin.codeLabel": "6-digit code",
  "signin.submit": "Sign in",
  "signin.resend": "Send a new code",
  "signin.wrongCode": "That code doesn't match. Check the email and try again.",
  "signin.codeExpired": "That code has expired. Send a new one.",

  "unlock.title": "Parent PIN",
  "unlock.body": "This device remembers {parent}. Type your parent PIN.",
  "unlock.who": "Who is opening parent mode?",
  "unlock.pinLabel": "Parent PIN (6 digits)",
  "unlock.submit": "Open",
  "unlock.wrong":
    "That PIN doesn't work. After 5 missed tries, you'll need to sign in by email again.",
  "unlock.locked": "Too many tries. Sign in again with a code sent by email.",
  "unlock.useEmail": "Get a code by email instead",
  "remember.title": "Stay signed in on this device?",
  "remember.body":
    "Choose a 6-digit parent PIN. For 30 days it opens parent mode here, without an email code. Resuming a paused session, consent and pairing still ask for a code.",
  "remember.pinLabel": "New parent PIN",
  "remember.confirmLabel": "Type it again",
  "remember.hint": "Not your child's PIN, and not easy to guess.",
  "remember.submit": "Stay signed in",
  "remember.skip": "Not now",
  "remember.mismatch": "The two PINs don't match.",
  "remember.trivial": "This PIN is too easy to guess. Choose another one.",
  "remember.childPin": "This is a child's PIN on this device. Choose another one.",
  "devices.title": "Remembered devices",
  "devices.empty": "None. After an email code, you can choose to stay signed in on a device.",
  "devices.this": "This device",
  "devices.lastUsed": "Last opened {date}",
  "devices.until": "valid until {date}",
  "devices.remove": "Remove",
  "devices.signOutAll": "Sign out everywhere",
  "devices.signOutAllBody":
    "This ends your parent sessions here and on every other device, and forgets every remembered device. You will need an email code next time.",
  "devices.signOutAllConfirm": "Yes, sign out everywhere",
  "devices.signOutAllCancel": "Cancel",

  "picker.title": "Which child?",
  "picker.empty": "No child profiles yet.",
  "picker.switch": "Switch child",

  "tab.progress": "Progress",
  "tab.goals": "Goals",
  "tab.consent": "Consent",
  "tab.alerts": "Alerts",
  "tab.alertsUnread": "Alerts ({count})",

  "progress.week": "This week",
  "progress.exercisesLabel": "Exercises",
  "progress.daysLabel": "Practice days",
  "progress.daysOf": "{count} of {days}",
  "progress.exercises.one": "{count} exercise",
  "progress.exercises.other": "{count} exercises",
  "progress.thisWeek": "{exercises} this week",
  "sessions.title": "Sessions",
  "sessions.empty":
    "No session in the last 7 days. After your child's next session, its summary appears here.",
  "sessions.minutes": "{count} min",
  "sessions.notes.strong": "A strong moment",
  "sessions.notes.watch": "Worth watching",
  "sessions.notes.question": "A question for dinner",
  "sessions.notes.source":
    "Written automatically from the session's figures below. Your child's messages are never used.",
  "sessions.plan": "The plan",
  "sessions.goal.climb": "Progress: {skill}",
  "sessions.goal.review": "Review: {skill}",
  "sessions.goal.done": "done",
  "sessions.goal.later": "for next time",
  "sessions.exercises.one": "{count} exercise finished",
  "sessions.exercises.other": "{count} exercises finished",
  "sessions.firstTry": "{count} on the first try",
  "sessions.withHints": "hints from Coach on {count}",
  "sessions.climbs": "Steps climbed",
  "sessions.steps.one": "+{count} step",
  "sessions.steps.other": "+{count} steps",
  "sessions.mastered": "mastered",
  "sessions.openPages": "See the pages",
  "sessions.closePages": "Hide the pages",
  "sessions.noPages": "No exercise was finished in this session.",
  "sessions.pagesNote": "Each try shows its result. Typed answers are not kept here.",
  "sessions.page": "Page {n}",
  "sessions.try": "Try {n}: {verdict}",
  "sessions.tryRight": "right",
  "sessions.tryNotYet": "not yet",
  "sessions.rightAnswer": "Expected answer: {answer}",
  "sessions.hint": "Coach's help went up to: {level}",
  "sessions.hint.socratic": "questions only",
  "sessions.hint.nudge": "a nudge",
  "sessions.hint.strategy": "the method",
  "sessions.hint.partialStep": "part of the working",
  "sessions.hint.workedExample": "a worked example",
  "progress.climbs": "Where each skill stands",
  "progress.climbsEmpty": "No skill practised yet.",
  "progress.invalid": "These cards couldn't be shown.",

  "goals.group.practice": "Practice",
  "goals.group.coach": "Coach",
  "goals.group.notify": "Tell me",
  "goals.subjects": "Subjects",
  "subject.math": "Maths",
  "subject.reasoning": "Reasoning",
  "subject.sciences": "Sciences",
  "subject.humanities": "Humanities",
  "goals.noSubject": "No subject chosen: practice can't start.",
  "goals.dailyMinutes": "Time per day",
  "goals.minutes": "{count} min",
  "goals.less": "Less",
  "goals.more": "More",
  "goals.quietHours": "Quiet hours",
  "goals.quietHint": "No practice between these times, every day.",
  "goals.quietStart": "Quiet hours start",
  "goals.quietEnd": "Quiet hours end",
  "goals.maxDifficulty": "Hardest level",
  "goals.level": "{level} of 10",
  "goals.maxDifficultyHint": "No exercise above this level. 1 is the easiest, 10 the hardest.",
  "goals.askParent": "Pause when frustrated",
  "goals.askParentHint": "When your child seems frustrated, practice pauses and you get an alert.",
  "goals.freeQuestions": "Free questions",
  "goals.freeQuestionsHint":
    "Questions outside the exercise, such as “why is the sky blue?”. Coach answers in a few sentences, with the same safety checks as the exercises.",
  "goals.oneBoard": "Try Coach's board (new)",
  "goals.oneBoardHint":
    "The lesson on one page: Coach explains, then the exercise and your child's working appear just below, with the reply box at the bottom. You can switch back at any time.",
  "goals.notifyNow": "Frustration: alert right away",
  "goals.digest": "Progress notes in the summary",
  "goals.weeklyEmail": "Weekly summary email",
  "goals.info": "About: {label}",
  "goals.autosave": "Changes save on their own",
  "goals.saving": "Saving…",
  "goals.saved": "Saved",
  "goals.invalid": "Some values are out of range. Please check them.",
  "goals.conflict":
    "These goals were changed on another device. Here is the latest version: check it and change it again if needed.",
  "goals.changedElsewhere": "These goals were just updated on another device.",
  "class.group": "In class right now",
  "class.level": "Class",
  "class.levelHint":
    "Your child's year at school (French system). Coach then knows which chapters the class can be on.",
  "class.levelNone": "Choose",
  "class.chapter": "Current chapter",
  "class.chapterNone": "Nothing in particular",
  "class.chaptersFailed": "The chapters couldn't be loaded. Please try again later.",
  "class.chosenByChild": "Chosen by your child on the lesson screen.",
  "class.test": "Next test",
  "class.testHint":
    "Before this date, practice reviews the chapter twice: about a week before, then in the last two days.",
  "class.testClear": "No test",
  "class.subject.math": "Maths",
  "class.subject.fra": "French",
  "class.subject.sci": "Science and technology",
  "class.subject.svt": "Life and earth sciences",
  "class.subject.pc": "Physics and chemistry",
  "class.subject.hg": "History and geography",
  "class.subject.emc": "Civics",
  "class.subject.tech": "Technology",

  "consent.title": "Consent history",
  "consent.empty": "No consent recorded.",
  "consent.version": "Notice version {version}",
  "consent.current": "Current",
  "consent.method.verified_email_invited_alpha": "Verified by email",
  "consent.needed": "The privacy notice changed (version {version}). Please review and accept it.",
  "consent.accept": "I accept the current notice",
  "consent.accepted": "Thank you, your consent is recorded.",
  "consent.upToDate": "Your consent is up to date.",
  "consent.currentSince": "Notice version {version}, accepted on {date}.",
  "consent.rights": "Your rights",
  "consent.withdraw": "Withdraw consent",
  "consent.export": "Export my child's data",
  "consent.soon": "Coming soon",

  "review.title": "Improving Coach",
  "review.label": "Use conversations to check quality and safety",
  "review.hint":
    "While this is on, we keep the conversation text for 30 days in our own monitoring system and review samples, some of them automatically with AI models from providers that keep no data, to find mistakes and keep Coach reliable and safe. It is on unless you switch it off. Switching it off applies to every child in the family from their next session. Safety alerts work either way.",
  "review.hintLabel": "About conversation review",
  "review.off": "Off since {date}.",

  "alerts.empty": "No alerts.",
  "alerts.emptyBody": "Coach will let you know here if something needs your attention.",
  "alerts.markRead": "Mark as read",
  "alerts.read": "Read",
  "alerts.now": "Urgent",
  "alerts.digest": "For the summary",
  "alerts.reason.safety_low": "Something your child said may need your attention.",
  "alerts.reason.safety_high": "Something your child said needs your attention now.",
  "alerts.reason.frustration": "Your child seemed frustrated.",
  "alerts.reason.plateau": "Your child has been stuck on a skill.",
  "alerts.reason.daily_cap": "Your child reached the daily practice limit.",
  "alerts.skill": "Skill: {skill}",
  "alerts.toast": "New alert",
  "alerts.toastPaused": "New alert: the session was paused.",
  "alerts.dismiss": "Dismiss",
  "alerts.pauseExplain":
    "Coach and practice are paused until tomorrow. We suggest you talk with your child first, then resume the session if you wish.",
  "alerts.resume": "Resume the session",
  "alerts.resumed": "The session is open again.",
  "alerts.notPaused": "The session is no longer paused.",
} as const;

export type ParentStringKey = keyof typeof en;

const fr: Readonly<Record<ParentStringKey, string>> = {
  "parent.open": "Parent",
  "parent.openLabel": "Ouvrir l'espace parent",
  "parent.brand": "Tutor by Probability Lab",
  "parent.title": "Espace parent",
  "parent.leave": "Retour à la leçon",
  "parent.loading": "Chargement…",
  "parent.retry": "Réessayer",
  "parent.offline": "Vous êtes hors ligne. Vérifiez la connexion et réessayez.",
  "parent.rateLimited": "Trop d'essais d'affilée. Patientez quelques minutes.",
  "parent.error": "Une erreur s'est produite. Veuillez réessayer.",

  "signin.title": "Connexion parent",
  "signin.body": "Pour ouvrir l'espace parent, nous vous envoyons un code par e-mail.",
  "signin.expired": "Par sécurité, reconnectez-vous avec un nouveau code.",
  "signin.emailLabel": "Votre adresse e-mail",
  "signin.invalidEmail": "Veuillez saisir une adresse e-mail valide.",
  "signin.sendCode": "M'envoyer un code",
  "signin.codeBody": "Saisissez le code à 6 chiffres envoyé à {email}.",
  "signin.codeLabel": "Code à 6 chiffres",
  "signin.submit": "Se connecter",
  "signin.resend": "Envoyer un nouveau code",
  "signin.wrongCode": "Ce code ne correspond pas. Vérifiez l'e-mail et réessayez.",
  "signin.codeExpired": "Ce code a expiré. Demandez-en un nouveau.",

  "unlock.title": "Code parent",
  "unlock.body": "Cet appareil a mémorisé le compte {parent}. Saisissez votre code parent.",
  "unlock.who": "Qui ouvre l'espace parent ?",
  "unlock.pinLabel": "Code parent (6 chiffres)",
  "unlock.submit": "Ouvrir",
  "unlock.wrong":
    "Ce code ne fonctionne pas. Après 5 essais manqués, il faudra vous reconnecter par e-mail.",
  "unlock.locked": "Trop d'essais. Reconnectez-vous avec un code reçu par e-mail.",
  "unlock.useEmail": "Recevoir plutôt un code par e-mail",
  "remember.title": "Rester connecté sur cet appareil ?",
  "remember.body":
    "Choisissez un code parent à 6 chiffres. Pendant 30 jours, il ouvre l'espace parent sur cet appareil sans code par e-mail. Reprendre une séance en pause, le consentement et l'appairage d'un appareil demandent toujours un code par e-mail.",
  "remember.pinLabel": "Nouveau code parent",
  "remember.confirmLabel": "Confirmez le code parent",
  "remember.hint": "Différent de celui de votre enfant, et difficile à deviner.",
  "remember.submit": "Rester connecté",
  "remember.skip": "Pas maintenant",
  "remember.mismatch": "Les deux codes ne correspondent pas.",
  "remember.trivial": "Ce code est trop facile à deviner. Choisissez-en un autre.",
  "remember.childPin": "C'est le code secret d'un enfant sur cet appareil. Choisissez-en un autre.",
  "devices.title": "Appareils mémorisés",
  "devices.empty":
    "Aucun. Après un code par e-mail, vous pouvez choisir de rester connecté sur un appareil.",
  "devices.this": "Cet appareil",
  "devices.lastUsed": "Dernière ouverture le {date}",
  "devices.until": "valable jusqu'au {date}",
  "devices.remove": "Retirer",
  "devices.signOutAll": "Se déconnecter partout",
  "devices.signOutAllBody":
    "Vos sessions parent se ferment ici et sur tous vos autres appareils, et plus aucun appareil ne se souvient de vous. Il faudra un code par e-mail la prochaine fois.",
  "devices.signOutAllConfirm": "Oui, se déconnecter partout",
  "devices.signOutAllCancel": "Annuler",

  "picker.title": "Quel enfant ?",
  "picker.empty": "Aucun profil enfant pour l'instant.",
  "picker.switch": "Changer d'enfant",

  "tab.progress": "Progression",
  "tab.goals": "Objectifs",
  "tab.consent": "Consentement",
  "tab.alerts": "Alertes",
  "tab.alertsUnread": "Alertes ({count})",

  "progress.week": "Cette semaine",
  "progress.exercisesLabel": "Exercices",
  "progress.daysLabel": "Jours d'entraînement",
  "progress.daysOf": "{count} sur {days}",
  "progress.exercises.one": "{count} exercice",
  "progress.exercises.other": "{count} exercices",
  "progress.thisWeek": "{exercises} cette semaine",
  "sessions.title": "Séances",
  "sessions.empty":
    "Aucune séance ces 7 derniers jours. Après la prochaine séance de votre enfant, son bilan apparaît ici.",
  "sessions.minutes": "{count} min",
  "sessions.notes.strong": "Un moment fort",
  "sessions.notes.watch": "Un point d'attention",
  "sessions.notes.question": "Une question pour le dîner",
  "sessions.notes.source":
    "Rédigé automatiquement à partir des chiffres de la séance ci-dessous. Les messages de votre enfant ne sont jamais utilisés.",
  "sessions.plan": "Le plan",
  "sessions.goal.climb": "Progresser : {skill}",
  "sessions.goal.review": "Réviser : {skill}",
  "sessions.goal.done": "atteint",
  "sessions.goal.later": "pour la prochaine fois",
  "sessions.exercises.one": "{count} exercice terminé",
  "sessions.exercises.other": "{count} exercices terminés",
  "sessions.firstTry": "dont {count} du premier coup",
  "sessions.withHints": "indices de Coach sur {count}",
  "sessions.climbs": "Étapes gagnées",
  "sessions.steps.one": "+{count} étape",
  "sessions.steps.other": "+{count} étapes",
  "sessions.mastered": "maîtrisée",
  "sessions.openPages": "Voir les pages",
  "sessions.closePages": "Masquer les pages",
  "sessions.noPages": "Aucun exercice terminé pendant cette séance.",
  "sessions.pagesNote":
    "Chaque essai montre son résultat. Les réponses tapées ne sont pas gardées ici.",
  "sessions.page": "Page {n}",
  "sessions.try": "Essai {n} : {verdict}",
  "sessions.tryRight": "juste",
  "sessions.tryNotYet": "pas encore",
  "sessions.rightAnswer": "Réponse attendue : {answer}",
  "sessions.hint": "Aide de Coach jusqu'à : {level}",
  "sessions.hint.socratic": "des questions seulement",
  "sessions.hint.nudge": "un coup de pouce",
  "sessions.hint.strategy": "la méthode",
  "sessions.hint.partialStep": "une partie du calcul",
  "sessions.hint.workedExample": "un exemple corrigé",
  "progress.climbs": "Où en est chaque compétence",
  "progress.climbsEmpty": "Aucune compétence travaillée pour l'instant.",
  "progress.invalid": "Ces cartes n'ont pas pu être affichées.",

  "goals.group.practice": "Entraînement",
  "goals.group.coach": "Coach",
  "goals.group.notify": "Me prévenir",
  "goals.subjects": "Matières",
  "subject.math": "Mathématiques",
  "subject.reasoning": "Raisonnement",
  "subject.sciences": "Sciences",
  "subject.humanities": "Sciences humaines",
  "goals.noSubject": "Aucune matière choisie : l'entraînement ne peut pas démarrer.",
  "goals.dailyMinutes": "Temps par jour",
  "goals.minutes": "{count} min",
  "goals.less": "Moins",
  "goals.more": "Plus",
  "goals.quietHours": "Heures de repos",
  "goals.quietHint": "Pas d'entraînement pendant ces heures, tous les jours.",
  "goals.quietStart": "Début des heures de repos",
  "goals.quietEnd": "Fin des heures de repos",
  "goals.maxDifficulty": "Niveau maximal",
  "goals.level": "{level} sur 10",
  "goals.maxDifficultyHint":
    "Aucun exercice au-dessus de ce niveau. 1 est le plus facile, 10 le plus difficile.",
  "goals.askParent": "Pause en cas de frustration",
  "goals.askParentHint":
    "Si votre enfant semble frustré, l'entraînement s'arrête et vous recevez une alerte.",
  "goals.freeQuestions": "Questions libres",
  "goals.freeQuestionsHint":
    "Des questions hors exercice, comme « pourquoi le ciel est-il bleu ? ». Coach répond en quelques phrases, avec les mêmes contrôles de sécurité que pour les exercices.",
  "goals.oneBoard": "Essayer le tableau de Coach (nouveau)",
  "goals.oneBoardHint":
    "La leçon sur une seule page : Coach explique, puis l'exercice et le brouillon de votre enfant apparaissent juste en dessous, avec la zone de réponse en bas. Vous pouvez revenir en arrière à tout moment.",
  "goals.notifyNow": "Frustration : alerte immédiate",
  "goals.digest": "Commentaires sur la progression dans le bilan",
  "goals.weeklyEmail": "Bilan par e-mail chaque semaine",
  "goals.info": "En savoir plus : {label}",
  "goals.autosave": "Enregistrement automatique",
  "goals.saving": "Enregistrement…",
  "goals.saved": "Enregistré",
  "goals.invalid": "Certaines valeurs sont hors limites. Vérifiez-les.",
  "goals.conflict":
    "Ces objectifs ont été modifiés sur un autre appareil. Voici la dernière version : vérifiez-la et modifiez-la si besoin.",
  "goals.changedElsewhere": "Ces objectifs viennent d'être modifiés sur un autre appareil.",
  "class.group": "En classe en ce moment",
  "class.level": "Classe",
  "class.levelHint":
    "La classe de votre enfant cette année. Coach sait alors quels chapitres la classe peut étudier.",
  "class.levelNone": "Choisir",
  "class.chapter": "Chapitre en cours",
  "class.chapterNone": "Rien de particulier",
  "class.chaptersFailed": "Les chapitres n'ont pas pu être chargés. Réessayez plus tard.",
  "class.chosenByChild": "Choisi par votre enfant sur l'écran de la séance.",
  "class.test": "Prochain contrôle",
  "class.testHint":
    "Avant cette date, l'entraînement revoit le chapitre deux fois : environ une semaine avant, puis les deux derniers jours.",
  "class.testClear": "Pas de contrôle",
  "class.subject.math": "Mathématiques",
  "class.subject.fra": "Français",
  "class.subject.sci": "Sciences et technologie",
  "class.subject.svt": "SVT",
  "class.subject.pc": "Physique-chimie",
  "class.subject.hg": "Histoire-géographie",
  "class.subject.emc": "EMC",
  "class.subject.tech": "Technologie",

  "consent.title": "Historique du consentement",
  "consent.empty": "Aucun consentement enregistré.",
  "consent.version": "Version de la notice {version}",
  "consent.current": "En vigueur",
  "consent.method.verified_email_invited_alpha": "Vérifié par e-mail",
  "consent.needed":
    "La notice de confidentialité a changé (version {version}). Merci de la lire et de l'accepter.",
  "consent.accept": "J'accepte la notice en vigueur",
  "consent.accepted": "Merci, votre consentement est enregistré.",
  "consent.upToDate": "Votre consentement est à jour.",
  "consent.currentSince": "Version {version} de la notice, acceptée le {date}.",
  "consent.rights": "Vos droits",
  "consent.withdraw": "Retirer mon consentement",
  "consent.export": "Exporter les données de mon enfant",
  "consent.soon": "Bientôt disponible",

  "review.title": "Améliorer Coach",
  "review.label": "Utiliser les conversations pour vérifier la qualité et la sécurité",
  "review.hint":
    "Tant que c'est activé, nous gardons le texte des conversations 30 jours dans notre propre outil de suivi et en relisons des échantillons, certains automatiquement avec des modèles d'IA de fournisseurs qui ne conservent aucune donnée, pour repérer les erreurs et que Coach reste fiable et sûr. C'est activé tant que vous ne le désactivez pas. Le désactiver vaut pour tous les enfants de la famille dès leur prochaine séance. Les alertes de sécurité fonctionnent dans les deux cas.",
  "review.hintLabel": "À propos de la relecture des conversations",
  "review.off": "Désactivé depuis le {date}.",

  "alerts.empty": "Aucune alerte.",
  "alerts.emptyBody": "Coach vous préviendra ici si quelque chose demande votre attention.",
  "alerts.markRead": "Marquer comme lue",
  "alerts.read": "Lue",
  "alerts.now": "Urgent",
  "alerts.digest": "Pour le résumé",
  "alerts.reason.safety_low":
    "Quelque chose que votre enfant a dit mérite peut-être votre attention.",
  "alerts.reason.safety_high":
    "Quelque chose que votre enfant a dit demande votre attention maintenant.",
  "alerts.reason.frustration": "Votre enfant semblait frustré.",
  "alerts.reason.plateau": "Votre enfant bloque sur une compétence.",
  "alerts.reason.daily_cap": "Votre enfant a atteint la limite d'entraînement du jour.",
  "alerts.skill": "Compétence : {skill}",
  "alerts.toast": "Nouvelle alerte",
  "alerts.toastPaused": "Nouvelle alerte : la séance a été mise en pause.",
  "alerts.dismiss": "Fermer",
  "alerts.pauseExplain":
    "Coach et l'entraînement sont en pause jusqu'à demain. Nous vous conseillons d'en parler d'abord avec votre enfant, puis de reprendre la séance si vous le souhaitez.",
  "alerts.resume": "Reprendre la séance",
  "alerts.resumed": "La séance a repris.",
  "alerts.notPaused": "La séance n'est plus en pause.",
};

export const parentDictionaries: Readonly<
  Record<Locale, Readonly<Record<ParentStringKey, string>>>
> = { en, fr };

export type ParentTranslate = (
  key: ParentStringKey,
  vars?: Readonly<Record<string, string | number>>,
) => string;

export function parentTranslate(
  locale: Locale,
  key: ParentStringKey,
  vars?: Readonly<Record<string, string | number>>,
): string {
  const template = parentDictionaries[locale][key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    if (value === undefined) return whole;
    return typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : value;
  });
}

/** Picks the `.one` or `.other` form of a counted string with the locale's plural rules. */
export function parentPlural(
  locale: Locale,
  key: "progress.exercises" | "sessions.exercises" | "sessions.steps",
  count: number,
): string {
  const form = new Intl.PluralRules(locale).select(count) === "one" ? "one" : "other";
  return parentTranslate(locale, `${key}.${form}`, { count });
}

export function useParentT(): ParentTranslate {
  const locale = useLocale();
  return useCallback<ParentTranslate>((key, vars) => parentTranslate(locale, key, vars), [locale]);
}
